import * as React from "react";
import * as ReactDOM from "react-dom";
import { cn } from "../../lib/utils";

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  className?: string;
  side?: "top" | "bottom" | "left" | "right";
  immediate?: boolean;
  delayLong?: boolean;
  autoPosition?: boolean;
}

const BUFFER = 12;
const GAP = 8;

function computePosition(
  triggerRect: DOMRect,
  tooltipEl: HTMLDivElement,
  preferredSide: "top" | "bottom" | "left" | "right"
): { top: number; left: number; side: "top" | "bottom" | "left" | "right" } {
  const tw = tooltipEl.offsetWidth;
  const th = tooltipEl.offsetHeight;
  const vh = window.innerHeight;
  const vw = window.innerWidth;

  const fits = {
    top: triggerRect.top - th - GAP >= BUFFER,
    bottom: triggerRect.bottom + th + GAP <= vh - BUFFER,
    left: triggerRect.left - tw - GAP >= BUFFER,
    right: triggerRect.right + tw + GAP <= vw - BUFFER,
  };

  const order: ("top" | "bottom" | "left" | "right")[] =
    preferredSide === "top"
      ? ["top", "bottom", "left", "right"]
      : preferredSide === "bottom"
        ? ["bottom", "top", "left", "right"]
        : preferredSide === "left"
          ? ["left", "right", "top", "bottom"]
          : ["right", "left", "top", "bottom"];

  let side = order.find((s) => fits[s]) || preferredSide;

  let top = 0;
  let left = 0;

  switch (side) {
    case "top":
      top = triggerRect.top - th - GAP;
      left = triggerRect.left + triggerRect.width / 2 - tw / 2;
      break;
    case "bottom":
      top = triggerRect.bottom + GAP;
      left = triggerRect.left + triggerRect.width / 2 - tw / 2;
      break;
    case "left":
      top = triggerRect.top + triggerRect.height / 2 - th / 2;
      left = triggerRect.left - tw - GAP;
      break;
    case "right":
      top = triggerRect.top + triggerRect.height / 2 - th / 2;
      left = triggerRect.right + GAP;
      break;
  }

  left = Math.max(BUFFER, Math.min(left, vw - tw - BUFFER));
  top = Math.max(BUFFER, Math.min(top, vh - th - BUFFER));

  return { top, left, side };
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  className,
  side = "top",
  immediate = false,
  delayLong = false,
}) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const triggerRef = React.useRef<HTMLDivElement | null>(null);
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);

  const showDelay = immediate ? 0 : delayLong ? 2000 : 700;

  const updatePosition = React.useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const { top, left } = computePosition(rect, tooltipRef.current, side);
    setPos({ top, left });
  }, [side]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (showDelay === 0) {
      setIsVisible(true);
    } else {
      timeoutRef.current = setTimeout(() => setIsVisible(true), showDelay);
    }
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsVisible(false);
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (!isVisible) return;
    const id = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(id);
  }, [isVisible, content, updatePosition]);

  const tooltipNode = isVisible
    ? ReactDOM.createPortal(
        <div
          ref={tooltipRef}
          style={{ top: pos.top, left: pos.left }}
          className="fixed z-[99999] px-3 py-2 text-xs bg-popover text-popover-foreground border rounded shadow-lg max-w-[280px] w-max leading-relaxed break-words animate-in fade-in-0 duration-150 pointer-events-none"
        >
          {content}
        </div>,
        document.body
      )
    : null;

  return (
    <div
      ref={triggerRef}
      className={cn("inline-flex", className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {tooltipNode}
    </div>
  );
};

export { Tooltip }; 